import { useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { computeMissedBlocks } from "../../lib/altPlans";

const DAY_OPTIONS = [
  { value: "monday", label: "Mon" },
  { value: "tuesday", label: "Tue" },
  { value: "wednesday", label: "Wed" },
  { value: "thursday", label: "Thu" },
  { value: "friday", label: "Fri" },
];

const fmtDate = (iso) => new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

/**
 * Shared form fields for an alternate plan (internship / off-site).
 * form = { plan_type, days, dates, arrival_time, departure_time, destination, notes }
 * `blocks` = full-day master-schedule blocks [{name, start_time, end_time}] used to
 * live-preview which blocks the chosen times cause the student to miss.
 */
export default function AltPlanFields({ form, setForm, blocks = [], testidPrefix = "" }) {
  const [newDate, setNewDate] = useState("");
  const planType = ["dates", "range"].includes(form.plan_type) ? form.plan_type : "weekly";

  const setMode = (mode) => setForm((p) => ({ ...p, plan_type: mode }));
  const toggleDay = (day) =>
    setForm((p) => ({ ...p, days: (p.days || []).includes(day) ? p.days.filter((d) => d !== day) : [...(p.days || []), day] }));
  const addDate = () => {
    if (!newDate) return;
    setForm((p) => ({ ...p, dates: [...new Set([...(p.dates || []), newDate])].sort() }));
    setNewDate("");
  };
  const removeDate = (d) => setForm((p) => ({ ...p, dates: (p.dates || []).filter((x) => x !== d) }));

  const fullDayOut = !!form.full_day_out;
  const missed = computeMissedBlocks(form.arrival_time || "", form.departure_time || "", blocks);

  return (
    <div className="space-y-4">
      {/* Full day out of building */}
      <label
        className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50/60 p-3 cursor-pointer"
        data-testid={`${testidPrefix}full-day-out-label`}
      >
        <input
          type="checkbox"
          checked={fullDayOut}
          onChange={(e) => setForm((p) => ({ ...p, full_day_out: e.target.checked }))}
          className="mt-0.5 h-4 w-4 accent-[#00305E]"
          data-testid={`${testidPrefix}full-day-out-checkbox`}
        />
        <span className="text-sm">
          <span className="font-medium text-[#00305E]">Full Day Out of Building</span>
          <span className="block text-xs text-muted-foreground mt-0.5">
            Student is off-site the entire day. Staff won't be prompted to mark attendance, and the day won't count against their attendance %. It still shows on the schedule as an alternate-plan day.
          </span>
        </span>
      </label>
      {/* Mode toggle */}
      <div className="space-y-2">
        <Label>Schedule Type</Label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMode("weekly")}
            className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${planType === "weekly" ? "bg-[#00305E] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            data-testid={`${testidPrefix}mode-weekly`}
          >
            Recurring weekdays
          </button>
          <button
            type="button"
            onClick={() => setMode("range")}
            className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${planType === "range" ? "bg-[#00305E] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            data-testid={`${testidPrefix}mode-range`}
          >
            Date range
          </button>
          <button
            type="button"
            onClick={() => setMode("dates")}
            className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${planType === "dates" ? "bg-[#00305E] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            data-testid={`${testidPrefix}mode-dates`}
          >
            Specific dates
          </button>
        </div>
      </div>

      {planType === "weekly" ? (
        <div className="space-y-2">
          <Label>Days *</Label>
          <div className="flex gap-2">
            {DAY_OPTIONS.map((d) => (
              <button
                key={d.value}
                type="button"
                onClick={() => toggleDay(d.value)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${(form.days || []).includes(d.value) ? "bg-[#00305E] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                data-testid={`${testidPrefix}day-toggle-${d.value}`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      ) : planType === "range" ? (
        <div className="space-y-2">
          <Label>Date range *</Label>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Start date</span>
              <Input
                type="date"
                value={form.start_date || ""}
                onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
                data-testid={`${testidPrefix}start-date-input`}
              />
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">End date</span>
              <Input
                type="date"
                value={form.end_date || ""}
                min={form.start_date || undefined}
                onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
                data-testid={`${testidPrefix}end-date-input`}
              />
            </div>
          </div>
          {form.start_date && form.end_date ? (
            form.end_date < form.start_date ? (
              <p className="text-xs text-red-600" data-testid={`${testidPrefix}range-error`}>End date must be on or after the start date.</p>
            ) : (
              <p className="text-xs text-muted-foreground">Applies every day from {fmtDate(form.start_date)} through {fmtDate(form.end_date)} (e.g. a multi-week trip).</p>
            )
          ) : (
            <p className="text-xs text-muted-foreground">Pick a start and end date — the plan applies to the whole span without adding each day.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <Label>Dates *</Label>
          <div className="flex gap-2">
            <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="w-44" data-testid={`${testidPrefix}date-input`} />
            <button
              type="button"
              onClick={addDate}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-[#00305E] text-white hover:bg-[#00305E]/90 disabled:opacity-50"
              disabled={!newDate}
              data-testid={`${testidPrefix}add-date-btn`}
            >
              Add
            </button>
          </div>
          {(form.dates || []).length > 0 ? (
            <div className="flex flex-wrap gap-2 pt-1">
              {(form.dates || []).map((d) => (
                <span key={d} className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-xs px-2.5 py-1" data-testid={`${testidPrefix}date-chip-${d}`}>
                  {fmtDate(d)}
                  <button type="button" onClick={() => removeDate(d)} className="text-amber-600 hover:text-amber-900" aria-label={`remove ${d}`} data-testid={`${testidPrefix}remove-date-${d}`}>×</button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Add one or more specific dates the student is off-site.</p>
          )}
        </div>
      )}

      {/* Arrival / Departure times */}
      {!fullDayOut && (
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Arrival Time</Label>
          <Input
            type="time"
            value={form.arrival_time || ""}
            onChange={(e) => setForm((p) => ({ ...p, arrival_time: e.target.value }))}
            data-testid={`${testidPrefix}arrival-time-input`}
          />
          <p className="text-xs text-muted-foreground">Leave blank if here at the normal start</p>
        </div>
        <div className="space-y-2">
          <Label>Departure Time</Label>
          <Input
            type="time"
            value={form.departure_time || ""}
            onChange={(e) => setForm((p) => ({ ...p, departure_time: e.target.value }))}
            data-testid={`${testidPrefix}departure-time-input`}
          />
          <p className="text-xs text-muted-foreground">Leave blank if here until dismissal</p>
        </div>
      </div>
      )}

      {/* Auto-computed blocks missed */}
      {!fullDayOut && (form.arrival_time || form.departure_time) && (
        <div className="rounded-md border bg-blue-50/60 border-blue-200 p-2.5 text-xs text-blue-800" data-testid={`${testidPrefix}missed-blocks`}>
          {missed.length > 0
            ? <>Misses <span className="font-semibold">{missed.join(", ")}</span> based on the Master Schedule block times.</>
            : <>Present for all blocks (arrives/leaves within block times).</>}
        </div>
      )}

      <div className="space-y-2">
        <Label>Destination *</Label>
        <Input value={form.destination} onChange={(e) => setForm((p) => ({ ...p, destination: e.target.value }))} placeholder="e.g., Bakery, Burlington High School" data-testid={`${testidPrefix}destination-input`} />
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Input value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Optional notes" data-testid={`${testidPrefix}notes-input`} />
      </div>
    </div>
  );
}
